import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdviseEngagement,
  AdviseSupplier,
  SupplierDirectoryRow,
  SupplierSelectionEntry,
  SupplierSelectionSaved
} from '../../../types/advise';
import {
  createAdviseSupplier,
  fetchAdviseSupplierDirectory,
  updateAdviseSupplier
} from '../../../Api/Advise/Suppliers';
import { updateAdviseSupplierSelection } from '../../../Api/Advise/Engagements';
import { refusalMessage } from '../../../utils/refusalMessage';
import { addError } from '../../../store/Error';
import { upsertAdviseSuppliers } from '../../../store/AdviseSupplier';
import { EngagementSupplierSelection } from '../../Compounds/Advise/SideBySideView';
import { supplierKey } from '../../Compounds/Advise/SideBySideView/supplierKey';
import { usePendingSaves } from './usePendingSaves';

type DebouncedSave = ReturnType<typeof usePendingSaves>['debouncedSave'];

const SAVE_FAILED = "Couldn't save that supplier. Please try again.";
const SAVE_REFUSED = 'A supplier on the shortlist is no longer available, so the list was reloaded.';

const keysFor = (supplierIds: string[], directory: SupplierDirectoryRow[]): string[] =>
  supplierIds.flatMap((id) => {
    const row = directory.find((candidate) => candidate.id === id);
    return row ? [supplierKey(row)] : [];
  });

const entryFor = (row: SupplierDirectoryRow): SupplierSelectionEntry =>
  row.id ? { id: row.id } : { sf_supplier_id: row.sf_supplier_id ?? '' };

const entriesFor = (keys: string[], directory: SupplierDirectoryRow[]): SupplierSelectionEntry[] => {
  const byKey = new Map(directory.map((row) => [supplierKey(row), row]));
  return keys.flatMap((key) => {
    const row = byKey.get(key);
    return row ? [entryFor(row)] : [];
  });
};

/** The supplier directory, loaded when the Supplier Selection tab asks, and the engagement's shortlist from it. */
export const useEngagementSupplierSelection = (
  engagement: AdviseEngagement | null,
  canEdit: boolean,
  debouncedSave: DebouncedSave,
  onSaved: (saved: SupplierSelectionSaved) => void
): EngagementSupplierSelection => {
  const engagementId = engagement?.id ?? null;
  const [directory, setDirectory] = useState<SupplierDirectoryRow[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[] | null>(null);
  const loadedFor = useRef(engagementId);
  const engagementRef = useRef(engagement);
  const directoryRef = useRef(directory);
  const selectedRef = useRef(selectedKeys);
  const onSavedRef = useRef(onSaved);

  useEffect(() => {
    engagementRef.current = engagement;
    onSavedRef.current = onSaved;
  }, [engagement, onSaved]);

  useEffect(() => {
    loadedFor.current = engagementId;
    directoryRef.current = null;
    selectedRef.current = null;
    setDirectory(null);
    setSelectedKeys(null);
    setFailed(false);
  }, [engagementId]);

  const showDirectory = useCallback((rows: SupplierDirectoryRow[]) => {
    directoryRef.current = rows;
    setDirectory(rows);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    if (!engagementId) return;
    setFailed(false);
    const { data, error } = await fetchAdviseSupplierDirectory();
    if (loadedFor.current !== engagementId) return;
    if (error || !data) {
      addError('advise-supplier', error?.message);
      setFailed(true);
      return;
    }
    addError('advise-supplier', undefined);
    showDirectory(data);
    // Only the first load reads the saved shortlist; after that the user's picks are newer than what was loaded.
    if (!selectedRef.current) {
      selectedRef.current = keysFor(engagementRef.current?.selected_suppliers ?? [], data);
      setSelectedKeys(selectedRef.current);
    }
  }, [engagementId, showDirectory]);

  // A Salesforce supplier's first pick makes its Advise record, which later saves then name by id.
  const showSaved = useCallback(
    (saved: SupplierSelectionSaved) => {
      const made = new Map(saved.suppliers.map((supplier) => [supplier.sf_supplier_id, supplier.id]));
      const rows = directoryRef.current;
      if (rows) {
        showDirectory(
          rows.map((row) => {
            const id = !row.id && row.sf_supplier_id ? made.get(row.sf_supplier_id) : undefined;
            return id ? { ...row, id } : row;
          })
        );
      }
      onSavedRef.current(saved);
    },
    [showDirectory]
  );

  // A supplier archived, or gone from Salesforce, since the tab loaded is refused on every save until it is dropped.
  const recoverRefusedShortlist = useCallback(
    async (engagementId: string, error: Parameters<typeof refusalMessage>[0]) => {
      await load();
      const listed = directoryRef.current ?? [];
      const picked = selectedRef.current ?? [];
      const kept = picked.filter((key) => listed.some((row) => supplierKey(row) === key));
      if (kept.length === picked.length) {
        // Nothing to drop, so the ticks go back to the shortlist the server holds.
        addError('advise-supplier', refusalMessage(error, SAVE_REFUSED));
        selectedRef.current = keysFor(engagementRef.current?.selected_suppliers ?? [], listed);
        setSelectedKeys(selectedRef.current);
        return { error: null };
      }
      addError('advise-supplier', SAVE_REFUSED);
      selectedRef.current = kept;
      setSelectedKeys(kept);
      const retried = await updateAdviseSupplierSelection(engagementId, entriesFor(kept, listed));
      if (retried.data && loadedFor.current === engagementId) showSaved(retried.data);
      return retried;
    },
    [load, showSaved]
  );

  const select = useCallback(
    (keys: string[], selected: boolean) => {
      const rows = directoryRef.current;
      if (!canEdit || !engagementId || !rows) return;
      const current = selectedRef.current ?? [];
      const next = selected
        ? [...current, ...keys.filter((key) => !current.includes(key))]
        : current.filter((key) => !keys.includes(key));
      selectedRef.current = next;
      setSelectedKeys(next);
      debouncedSave(`supplierSelection:${engagementId}`, async () => {
        // Built when sent, so a Salesforce supplier an earlier save gave an Advise record is named by that id.
        const result = await updateAdviseSupplierSelection(
          engagementId,
          entriesFor(next, directoryRef.current ?? rows)
        );
        if (loadedFor.current !== engagementId) return result;
        if (result.data) showSaved(result.data);
        if (result.error?.errorResponse?.response?.status !== 422) return result;
        return recoverRefusedShortlist(engagementId, result.error);
      });
    },
    [canEdit, engagementId, debouncedSave, showSaved, recoverRefusedShortlist]
  );

  const saveSupplier = useCallback(
    async (supplier: Partial<AdviseSupplier>): Promise<string | null> => {
      if (!canEdit) return null;
      const { data, error } = supplier.id
        ? await updateAdviseSupplier(supplier.id, supplier)
        : await createAdviseSupplier(supplier);
      if (error || !data) return refusalMessage(error, SAVE_FAILED, SAVE_FAILED);
      upsertAdviseSuppliers([data]);
      // The directory merges a linked supplier into its Salesforce row, so the API's list is the one to show.
      await load();
      return null;
    },
    [canEdit, load]
  );

  return useMemo(
    () => ({ directory, failed, load, selectedKeys: selectedKeys ?? [], select, saveSupplier }),
    [directory, failed, load, selectedKeys, select, saveSupplier]
  );
};
